import config from 'config';
import mediator, { ACTIONS } from 'mediator';
import fastdom from 'fastdom';
import { find, has, includes } from 'lodash';
import svgClasses from 'lib/common/svgClasses';
import renderCustomIcon from 'lib/seats/renderCustomIcon';
import { getSeatType, getAvailableSeatTypes } from 'lib/seats/seatTypes';
import features from 'lib/common/features';
import { pricesForZone } from 'services/Pricer';

const CLASS_NAMES = {
  base: 'seat',
  selected: 'seat--selected',
  hidden: 'seat--hidden',
  killed: 'seat--killed',
  customKilled: 'seat--killed-custom-icon'
};

function getClassNames(data) {
  let classNames = [CLASS_NAMES.base];
  let statusCode = data.seat_status.status_code;
  let seatType = getSeatType(statusCode);

  classNames.push(`seat--${seatType.slug}`);

  /**
     * Seats with specific status codes should be hidden
     */
  if (
    includes(config.seatTypes.hiddenStatusCodes, data.seat_status.status_code)
  ) {
    classNames.push(CLASS_NAMES.killed);
  }

  /**
     * Apply status class based on seat type id
     */
  let seatTypes = getAvailableSeatTypes();
  seatTypes.forEach(function(type) {
    if (
      has(type, 'seatTypeIds') &&
      includes(type.seatTypeIds, data.seat_type)
    ) {
      classNames.push(`seat--${seatType.slug}`);
    }
  });

  return classNames;
}

function clickHandler(el, data) {
  el.addEventListener('click', function() {
    mediator.publish(ACTIONS.SEAT_SELECTED, { seatEl: el, seatData: data });
  });
}

function injectCustomIconElement(type, seat, classNames) {
  let newEl;
  try {
    newEl = renderCustomIcon(type.customName, seat.el, classNames);
  } catch (e) {} // eslint-disable-line no-empty

  if (newEl) {
    // Set an id on the new seat icon for later retrieval.
    newEl.id = `syos-custom-icon-${seat.data.seat_no}`;

    // Hide the old element
    svgClasses.addClass(seat.el, CLASS_NAMES.killed);
    svgClasses.addClass(seat.el, CLASS_NAMES.customKilled);

    // Re-add the click handler to the new element
    clickHandler(newEl, seat.data);

    return newEl;
  } else {
    return seat.el;
  }
}

function matchIconForHoldCode(customSeatTypes, seatData) {
  return find(customSeatTypes, function(type) {
    return (
      has(type, 'customName') &&
      has(type, 'holdCodes') &&
      includes(type.holdCodes, seatData.hc_no)
    );
  });
}

function matchingIconForSeatType(customSeatTypes, seatData) {
  return find(customSeatTypes, function(type) {
    return (
      has(type, 'customName') &&
      has(type, 'seatTypeIds') &&
      includes(type.seatTypeIds, seatData.seat_type)
    );
  });
}

function matchFirstCustomIcon(customSeatTypes, seatData) {
  var iconForHoldCode = matchIconForHoldCode(customSeatTypes, seatData);

  if (iconForHoldCode) {
    return iconForHoldCode;
  }

  var iconForSeatType = matchingIconForSeatType(customSeatTypes, seatData);
  if (iconForSeatType) {
    return iconForSeatType;
  }
}

function enhanceSeatEls(seats, prices = []) {
  var customSeatTypes = getAvailableSeatTypes().filter(function(x) {
    return x.method === 'custom';
  });

  seats.map(function(seat) {
    if (seat.data && seat.el) {
      if (features.isEnabled('blackOutSeatsWithoutPrices')) {
        if (pricesForZone(prices, seat.data.zone_no).length === 0) {
          seat.data.seat_status = features.getOption(
            'blackOutSeatsWithoutPrices',
            'blackoutStatus'
          ) || {
            back_color: '0',
            description: 'Blacked Out',
            fore_color: '0',
            id: '6',
            status_code: 'BLK',
            status_legend: 'X',
            status_priority: '70'
          };
        }
      }

      let classNames = getClassNames(seat.data);

      classNames.forEach(function(name) {
        svgClasses.addClass(seat.el, name);
      });

      clickHandler(seat.el, seat.data);

      let customIconMatch = matchFirstCustomIcon(customSeatTypes, seat.data);
      if (customIconMatch) {
        seat.el = injectCustomIconElement(customIconMatch, seat, classNames);
      }

      if (
        features.isEnabled('podSeats') &&
        features.getOption('podSeats', 'minimumPerZones')
      ) {
        let minimumForBoxes = features.getOption('podSeats', 'minimumPerZones');

        minimumForBoxes.some(el => {
          if (el.zone_ids.includes(seat.data.zone_no)) {
            if (seat.el.tagName === 'g') {
              seat.el.children[1].style.stroke = el.color;
            }
            return (seat.el.style.stroke = el.color);
          }
        });
      }
    }

    return seat;
  });

  return seats;
}

function priceFilters(seats) {
  mediator.subscribe(ACTIONS.PRICE_FILTER_SELECTED, function(payload) {
    const selected = payload.selected;

    if (selected.length) {
      const otherSeats = seats.filter(seat => {
        return has(seat, 'data.zone_no')
          ? selected.indexOf(seat.data.zone_no) === -1
          : false;
      });

      fastdom.mutate(function() {
        seats.forEach(function(seat) {
          svgClasses.removeClass(seat.el, CLASS_NAMES.hidden);
        });

        otherSeats.forEach(function(seat) {
          svgClasses.addClass(seat.el, CLASS_NAMES.hidden);
        });
      });
    } else {
      fastdom.mutate(function() {
        seats.forEach(function(seat) {
          svgClasses.removeClass(seat.el, CLASS_NAMES.hidden);
        });
      });
    }
  });
}

/**
 * Toggle seat selection style
 * Need to a bit verbose with checks to avoid cases where
 * multiple events are triggered and cause double toggling
 */
function toggleSelected(seatEl, isSelected) {
  if (isSelected && !svgClasses.hasClass(seatEl, CLASS_NAMES.selected)) {
    svgClasses.addClass(seatEl, CLASS_NAMES.selected);
  }

  if (!isSelected && svgClasses.hasClass(seatEl, CLASS_NAMES.selected)) {
    svgClasses.removeClass(seatEl, CLASS_NAMES.selected);
  }
}

function selectionListeners() {
  mediator.subscribe(ACTIONS.SEAT_ADDED, function(seat) {
    seat && toggleSelected(seat.seatEl, true);
  });
  mediator.subscribe(ACTIONS.SEAT_TOGGLE_SELECTED, function(seat) {
    seat && toggleSelected(seat.seatEl, true);
  });
  mediator.subscribe(ACTIONS.SEAT_DESELECTED, function(seat) {
    seat && toggleSelected(seat.seatEl, false);
  });
}

export default function(seats, prices = []) {
  var enhancedSeats = enhanceSeatEls(seats, prices);

  priceFilters(seats);
  selectionListeners();

  return enhancedSeats;
}



// WEBPACK FOOTER //
// ./src/javascripts/app/behaviours/enhanceSeats.js